import React from 'react';
import { Video, Upload, Play, CheckCircle2, Loader2, Link2 } from 'lucide-react';
import GphRichBlock from '../GphRichBlock';
import { mapSymptomToDrillGoal, mapObjectiveToDrillGoal, SYMPTOM_ALLOWED_CATEGORIES } from '../../../services/gph/gphCoherenceGuard';

const GOAL_PRESENTATION = {
  mi_analysis_x1: {
    title: 'Análise Tática de Duelo (x1)',
    subtitle: 'Foco: decisão de combate, timing de cover e motivo da eliminação.'
  },
  mi_analysis_aim: {
    title: 'Análise de Estabilidade de Mira',
    subtitle: 'Foco: precisão de spray, recoil e micro-correções na retícula.'
  },
  mi_analysis_movement: {
    title: 'Análise de Movimentação e Cover',
    subtitle: 'Foco: posicionamento, strafe e rotação sob fogo cruzado.'
  },
  mi_analysis_replay: {
    title: 'Diagnóstico de Replay Clássico',
    subtitle: 'Foco: identificação do erro decisivo na jogada enviada.'
  },
  mi_analysis_gameplay: {
    title: 'Análise Completa de Gameplay 360°',
    subtitle: 'Análise geral de combate: mira, posicionamento e tomada de decisão.'
  }
};

export default function MiAnalysisSkillAdapter({ agent, context = {} }) {
  const capabilityId = context.capabilityId || 'mi_analysis_gameplay';
  const presentation = GOAL_PRESENTATION[capabilityId] || GOAL_PRESENTATION.mi_analysis_gameplay;

  const {
    selectedVideo,
    videoPreview,
    selectVideo,
    startMiAnalysis,
    isProcessing,
    coachStatus,
    coachAnalysisResult
  } = agent.host;

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 200 * 1024 * 1024) {
        alert('O vídeo deve ter menos de 200MB.');
        return;
      }
      selectVideo?.(file);
    }
  };

  const handleStart = () => {
    if (selectedVideo && typeof startMiAnalysis === 'function') {
      startMiAnalysis(selectedVideo, context);
    }
  };

  const isJobRunning = isProcessing || ['uploading', 'queued', 'processing'].includes(String(coachStatus));

  return (
    <GphRichBlock
      eyebrow="Análise de gameplay"
      title={presentation.title}
      subtitle={presentation.subtitle}
    >
      <div className="space-y-6 pt-2 text-left">
        {/* Validação vinculada ao último diagnóstico/treino (Video Validation Guard) */}
        {context.validationGoal && (
          <p className="text-[11px] text-lime-300 leading-relaxed m-0 flex items-start gap-1.5 font-mono">
            <Link2 size={13} className="shrink-0 mt-0.5" />
            <span>Validação vinculada: {context.validationGoal}</span>
          </p>
        )}
        {/* Final Report (if concluded) */}
        {coachAnalysisResult ? (
          <div className="space-y-6">
            <div className="flex items-center gap-2 text-lime-400 font-mono text-[11px] font-bold uppercase tracking-wider">
              <CheckCircle2 size={15} /> Relatório de Inteligência MI Gerado
            </div>
            
            <h4 className="text-sm font-bold text-white m-0">
              {coachAnalysisResult.title || 'Diagnóstico da Jogada Analisada'}
            </h4>
            
            <p className="text-sm text-slate-300 leading-relaxed font-sans m-0">
              {coachAnalysisResult.summary || coachAnalysisResult.diagnosis || 'Análise processada. Identificamos oportunidades para melhorar seu tempo de resposta e controle sob pressão.'}
            </p>
            
            {coachAnalysisResult.keyError && (
              <div className="border-l-2 border-rose-500 pl-4 py-1">
                <strong className="font-bold uppercase block text-[10px] text-rose-400 tracking-wider mb-1">Erro Decisivo</strong>
                <p className="text-xs text-rose-200 m-0 leading-relaxed font-sans">{coachAnalysisResult.keyError}</p>
              </div>
            )}
            
            {coachAnalysisResult.recommendation && (
              <div className="border-l-2 border-lime-500 pl-4 py-1">
                <strong className="font-bold uppercase block text-[10px] text-lime-400 tracking-wider mb-1">Ajuste Recomendado</strong>
                <p className="text-xs text-lime-200 m-0 leading-relaxed font-sans">{coachAnalysisResult.recommendation}</p>
              </div>
            )}
            
            <div className="border-t border-white/5 pt-5 mt-6 flex flex-wrap gap-3 justify-end">
              <button
                type="button"
                className="gph-btn gph-btn--primary text-xs"
                onClick={() => {
                  // Coherence Guard: o treino nasce do contexto real da análise
                  // (diagnóstico vinculado ou objetivo), nunca de 'cover' fixo.
                  const fromDiagnosis = context.linkedTo?.type === 'diagnosis' ? context.linkedTo.symptom : null;
                  agent.runCapability('training_single_drill', fromDiagnosis
                    ? {
                      drillGoal: mapSymptomToDrillGoal(fromDiagnosis),
                      allowedCategories: SYMPTOM_ALLOWED_CATEGORIES[fromDiagnosis] || null,
                      fromDiagnosis
                    }
                    : { drillGoal: mapObjectiveToDrillGoal(String(context.validationGoal || coachAnalysisResult?.keyError || 'cover').toLowerCase()) });
                }}
              >
                Treinar essa situação
              </button>
              <button
                type="button"
                className="gph-btn gph-btn--ghost text-xs"
                onClick={() => selectVideo?.(null)}
              >
                Analisar outro vídeo
              </button>
            </div>
          </div>
        ) : (
          /* Selection / Upload State */
          <div className="space-y-4">
            {isJobRunning ? (
              <div className="py-8 text-center space-y-4">
                <Loader2 size={28} className="text-lime-400 animate-spin mx-auto" />
                <div className="space-y-1">
                  <h4 className="text-xs font-bold uppercase tracking-wider text-white m-0">Processando Vídeo na Engine MI</h4>
                  <p className="text-xs text-slate-400 m-0 font-sans">Aguarde, a IA está avaliando sua jogada...</p>
                </div>
              </div>
            ) : selectedVideo ? (
              <div className="space-y-4">
                <div className="flex items-center justify-between border-b border-white/5 pb-4">
                  <div className="flex items-center gap-2.5 overflow-hidden font-sans">
                    <Video size={18} className="text-lime-400 shrink-0" />
                    <div className="truncate">
                      <span className="text-xs font-mono font-bold text-white block truncate">{selectedVideo.name}</span>
                      <span className="text-[10px] text-slate-400">{(selectedVideo.size / (1024 * 1024)).toFixed(1)} MB</span>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => selectVideo(null)}
                    className="text-xs text-rose-400 hover:text-rose-300 font-mono px-2 py-1 underline"
                  >
                    Trocar
                  </button>
                </div>

                <button
                  type="button"
                  onClick={handleStart}
                  className="gph-btn gph-btn--primary w-full py-2.5 text-xs font-bold uppercase tracking-wider flex items-center justify-center gap-2"
                >
                  <Play size={14} fill="currentColor" /> Iniciar Análise MI
                </button>
              </div>
            ) : (
              <div className="border border-dashed border-white/10 hover:border-lime-500/30 rounded-lg p-8 text-center transition-all bg-white/[0.01]">
                <input
                  type="file"
                  id="mi-chat-upload"
                  accept="video/mp4,video/mov,video/quicktime,video/webm"
                  onChange={handleFileChange}
                  className="hidden"
                />
                <label htmlFor="mi-chat-upload" className="cursor-pointer space-y-3 block font-sans">
                  <Upload size={24} className="text-lime-400 mx-auto" />
                  <span className="text-xs font-bold uppercase text-white block tracking-wider">Envie seu vídeo de gameplay</span>
                  <span className="text-[11px] text-slate-400 block">MP4, MOV ou WEBM até 200MB</span>
                </label>
              </div>
            )}
          </div>
        )}
      </div>
    </GphRichBlock>
  );
}
